import { Button, Col, Input, Select, Row, Form } from "antd";
import useGet from "../../../global/hooks/useGet";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath, HiOutlineViewColumns } from "react-icons/hi2";
import { useEffect, useState, useCallback, useRef } from "react";
import CommonModal from "../../../global/components/CommonModal";
import { FaColumns } from "react-icons/fa";
import MultiSelect from "../../../global/components/inputWidgets/Transfer/MultiSelect";
import {
  getOemNo,
  getUserData,
  hasRole,
} from "../../../global/hooks/getUserRole";
import dayjs from "dayjs";

const LicenseDeviceOptions = [
  {
    label: "Channel",
    value: "Channel",
  },
  {
    label: "Facegate",
    value: "Facegate",
  },
  {
    label: "Lockset",
    value: "Lockset",
  },
  {
    label: "Trilogy Lockset",
    value: "ContLock",
  },
  {
    label: "OSDP Door",
    value: "Subnode",
  },
  {
    label: "Intercom",
    value: "Intercom",
  },
  {
    label: "Directory",
    value: "Sicucom",
  },
  {
    label: "Invitation",
    value: "Invitation",
  },
  // {
  //   label: "Storage",
  //   value: "Storage",
  // },
];

export const DeviceFilter = ({
  filter,
  handleFilterChange,
  handleFilter,
  handleReset,
  setShowModal,
  selectedRowKeys,
  setFilter,
  params,
  data,
  setColumnsValues,
}) => {
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/devicelogs/options/`;
  const optionDeviceUrl = `${process.env.REACT_APP_API_URL}/api/devices/options/`;
  const [selectedDeviceFilter, setSelectedDeviceFilter] = useState(null);
  const [showCommonModal, setShowCommonModal] = useState(false);

  const [form] = Form.useForm();
  // console.log(form.getFieldsValue());
  const {
    data: optionsData,
    loading: optionDataLoading,
    error: optionDataError,
  } = useGet(optionUrl);
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //

  const [visibleColumns, setVisibleColumns] = useState([]);
  const initializedRef = useRef(false);
  const localColumnValues =
    JSON.parse(localStorage.getItem("column"))?.find(
      (item) => item?.user_id === getUserData()?.user_id,
    )?.column_data || {};

  // Get default columns based on role permissions
  const getDefaultColumns = useCallback(() => {
    // Default columns matching Columns.js structure
    const allDefaultColumns = [
      "LogNo",
      "LogTime",
      "Account",
      "DeviceName",
      "Oem",
      "Dealer",
      "Customer",
      "NodeType",
      "Mac",
      "Product",
      "SiteType",
      "Model",
      "Type",
      "Options",
      "Devices",
    ];

    // Note: LogNo is included in defaults but was filtered in original code
    // Keeping it included as it's visible in Columns.js
    return allDefaultColumns;
  }, []);

  // Generate column options from data
  const getColumnOptions = () => {
    if (!data?.data || data.data.length === 0) {
      // If no data, return default columns as options
      return getDefaultColumns().map((key) => ({
        id: key,
        value: key,
        label: key.replace(/([A-Z])/g, " $1").trim(),
      }));
    }

    const sampleObject = data.data[0];
    return Object.keys(sampleObject).map((key) => ({
      id: key, // "1", "2", "3", etc.
      value: key, // "DeviceNo", "DeviceName", etc.
      label: key.replace(/([A-Z])/g, " $1").trim(),
    }));
  };

  // Initialize with just column names
  useEffect(() => {
    // If we already have saved columns, use them
    if (localColumnValues?.Log) {
      if (!initializedRef.current) {
        setVisibleColumns(localColumnValues.Log);
        setColumnsValues(localColumnValues.Log);
        initializedRef.current = true;
      }
      return;
    }

    // If already initialized and no saved columns, don't reinitialize
    if (initializedRef.current) {
      return;
    }

    // Handle both cases: when data exists and when it doesn't
    let allColumnIds;

    if (data?.data && data.data.length > 0) {
      // Use columns from actual data (keeping original filter logic)
      allColumnIds = Object.keys(data.data[0])
        .filter((item) => !["LogNo"].includes(item))
        .map((key, index) => key);
    } else {
      // No data available - use default columns (excluding LogNo to match original filter)
      allColumnIds = getDefaultColumns().filter(
        (item) => !["LogNo"].includes(item),
      );
    }

    setVisibleColumns(allColumnIds);
    setColumnsValues(allColumnIds);
    initializedRef.current = true;
  }, [data, getDefaultColumns, localColumnValues]);

  const dealerOptionsUrl = filter?.OemNo
    ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${filter?.OemNo}/dealers/`
    : !hasRole(1, 2, 3) && getOemNo()
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${getOemNo()}/dealers/`
      : null;

  const {
    data: dealerOptions,
    loading: dealerOptionsLoading,
    error: dealerOptionsError,
  } = useGet(dealerOptionsUrl);

  const {
    data: optionDeviceData,
    loading: optionDeviceDataLoading,
    error: optionDeviceDataError,
    refetch: optionDataRefetch,
  } = useGet(optionDeviceUrl);

  const custmerOptionsUrl =
    filter?.DealerNo &&
    `${process.env.REACT_APP_API_URL}/api/dealers/${filter?.OemNo}/customers/`;

  const {
    data: custmerOptions,
    loading: custmerOptionsLoading,
    error: custmerOptionsError,
  } = useGet(custmerOptionsUrl);

  const modifiedProducts = getModifiedResponse(optionsData, "products");
  const modifiedModels = getModifiedResponse(optionsData, "models");
  const modifiedLicenseOptions = getModifiedResponse(optionsData, "options");
  const modifiedSiteTypes = getModifiedResponse(optionDeviceData, "sitetype");
  const modifiedNodeTypes = getModifiedResponse(optionDeviceData, "nodetype");
  // Map device labels to their API values
  const deviceLabelToValueMap = {
    Channel: "Channel",
    Lockset: "Lockset",
    Facegate: "Facegate",
    "OSDP Door": "Subnode",
    "OSDP Device": "Subnode",
    "Trilogy Lockset": "ContLock",
    Intercom: "Intercom",
    "Directory Extension": "Sicucom",
    Invitation: "Invitation",
    "Mobile Credential": "Invitation", // Handle legacy label
  };

  const modifiedLicenseDevices = getModifiedResponse(
    optionsData,
    "devices",
  ).map((item) => {
    const label = item.label;
    // Get the API value: use mapped value if exists, otherwise use the label itself
    const apiValue = deviceLabelToValueMap[label] || label;
    return {
      value: apiValue,
      label: label,
    };
  });
  // console.log(modifiedNodeTypes);

  // const modifiedLicenseDevices = getModifiedResponse(optionsData, "devices");
  const modifiedOems = getModifiedResponse(optionsData, "oems");
  // const modifiedProductions = getModifiedResponse(optionsData, "productions");
  const modifiedDealers = getModifiedResponse(dealerOptions, null);
  const modifiedCustomers = getModifiedResponse(custmerOptions, null);

  // const createModifiedOptions = (data, key) => [
  //   // { label: "N/A", value: null },
  //   ...getModifiedResponse(data, key),
  // ];

  // const modifiedDeviceOptions = {
  //   Channel: createModifiedOptions(optionDeviceData, "channels"),
  //   Facegate: createModifiedOptions(optionDeviceData, "facegates"),
  //   Lockset: createModifiedOptions(optionDeviceData, "locksets"),
  //   ContLock: createModifiedOptions(optionDeviceData, "contlocks"),
  //   Subnode: createModifiedOptions(optionDeviceData, "subnodes"),
  //   Intercom: createModifiedOptions(optionDeviceData, "intercoms"),
  //   Sicucom: createModifiedOptions(optionDeviceData, "sicucoms"),
  //   Invitation: createModifiedOptions(optionDeviceData, "invitations"),
  //   Storage: createModifiedOptions(optionDeviceData, "storages"),
  // };

  function findMatchingItem(queryObj, optionsArray) {
    // Get all keys from query object (excluding common query params)
    const excludeKeys = ["limit", "sort_by", "order"];
    const deviceKeys = Object.keys(queryObj).filter(
      (key) => !excludeKeys.includes(key),
    );

    // For each device key in the query object
    for (const key of deviceKeys) {
      // Find matching item in options array (case-insensitive comparison)
      const matchingItem = optionsArray.find((item) => item.value === key);

      if (matchingItem) {
        return {
          queryKey: key,
          queryValue: queryObj[key],
          matchedItem: matchingItem,
        };
      }
    }

    return null; // No match found
  }

  // const {
  //   channels: modifiedChannels,
  //   facegates: modifiedFacegates,
  //   locksets: modifiedLocksets,
  //   contLocks: modifiedContLocks,
  //   subnodes: modifiedSubnodes,
  //   intercoms: modifiedIntercoms,
  //   sicucoms: modifiedSicucoms,
  //   invitations: modifiedInvitations,
  //   storages: modifiedStorages,
  // } = modifiedDeviceOptions;

  useEffect(() => {
    if (modParams) {
      const matchedParamDevices = findMatchingItem(
        modParams,
        LicenseDeviceOptions,
      );
      if (matchedParamDevices?.matchedItem) {
        setSelectedDeviceFilter(matchedParamDevices?.matchedItem?.value);
      }
      if (matchedParamDevices?.queryValue && selectedDeviceFilter) {
        setFilter({
          ...filter,
          [selectedDeviceFilter]: Number(matchedParamDevices?.queryValue),
        });
      }
    }
  }, [selectedDeviceFilter]);

  const onSearch = (value) => {};

  const handleFormChange = (changedValues, allValues) => {
    // Update filter state when form values change
    Object.keys(changedValues).forEach((key) => {
      if (key === "LogTime") {
        setFilter({ ...filter, LogTime: changedValues[key] });
      }
    });
  };

  useEffect(() => {
    const formValues = {};

    if (modParams?.LogTime) {
      formValues.LogTime = dayjs.unix(Number(modParams.LogTime));
    }

    // Set form values without triggering onChange
    if (Object.keys(formValues).length > 0) {
      form.setFieldsValue(formValues);
    }
  }, [params]);

  // console.log(modifiedLicenseOptions);

  return (
    <>
      <CommonModal
        isVisible={showCommonModal}
        onClose={() => setShowCommonModal(false)}
        width={800}
        title="Column"
        icon={FaColumns}
        onConfirm={() => {
          setColumnsValues(visibleColumns);

          // Get existing data from localStorage
          const existingData = localStorage.getItem("column");
          let columnData = existingData ? JSON.parse(existingData) : [];

          const currentUserId = getUserData()?.user_id;

          // Find if current user already exists in the array
          const userIndex = columnData?.findIndex(
            (item) => item?.user_id === currentUserId,
          );

          if (userIndex !== -1) {
            // User exists - merge with existing column_data to preserve other pages
            columnData[userIndex].column_data = {
              ...columnData[userIndex].column_data, // Keep existing data (Device, Log, etc.)
              Log: visibleColumns, // Update only Log
            };
          } else {
            // User doesn't exist - create new entry
            columnData.push({
              user_id: currentUserId,
              column_data: { Log: visibleColumns },
            });
          }

          // Save updated data back to localStorage
          localStorage.setItem("column", JSON.stringify(columnData));

          setShowCommonModal(false);
        }}
        content={
          <MultiSelect
            name="Column"
            label={`Search`}
            value={visibleColumns}
            options={getColumnOptions()}
            onChange={(name, selected) => {
              console.log(name, selected);
              setVisibleColumns(selected);
            }}
          />
        }
      />
      <Form className="" form={form} onValuesChange={handleFormChange}>
        <div className="mt-2">
          <Row gutter={[16, 16]}>
            {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Input
              className="input_text_3 mr10 !h-full"
              name="LogNo"
              placeholder="No"
              value={filter.LogNo}
              onChange={handleFilterChange}
            />
          </Col> */}
            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
              <CustomDateTimePicker
                name="LogTime"
                placeholder="Log Time"
                format="YYYY-MM-DD HH:mm" // 24-hour format
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
              <Input
                className="input_text_3 mr10 !h-full"
                name="AccountName"
                placeholder="Account Name"
                value={filter.AccountName}
                onChange={handleFilterChange}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
              <Input
                className="input_text_3 mr10 !h-full"
                name="DeviceName"
                placeholder="Device Name"
                value={filter.DeviceName}
                onChange={handleFilterChange}
              />
            </Col>
            {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              showSearch
              placeholder="Production"
              value={filter.ProductionNo || undefined}
              optionFilterProp="ProductionNo"
              onChange={(value) =>
                setFilter({ ...filter, ProductionNo: value })
              }
              onSearch={onSearch}
              options={modifiedProductions}
              style={{ width: "100%" }}
              allowClear
            />
          </Col> */}
            {hasRole(1, 2, 3) && (
              <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Select
                  showSearch
                  placeholder="OEM"
                  value={
                    filter.OemNo || typeof filter.OemNo === "number"
                      ? Number(filter.OemNo)
                      : null
                  }
                  optionFilterProp="OemNo"
                  onChange={(value) => setFilter({ ...filter, OemNo: value })}
                  onSearch={onSearch}
                  options={modifiedOems}
                  style={{ width: "100%" }}
                  allowClear
                />
              </Col>
            )}
            {hasRole(1, 2, 3, 4, 5, 6) && (
              <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Select
                  showSearch
                  placeholder="Dealer"
                  value={filter.DealerNo ? Number(filter.DealerNo) : undefined}
                  optionFilterProp="DealerNo"
                  onChange={(value) =>
                    setFilter({ ...filter, DealerNo: value })
                  }
                  onSearch={onSearch}
                  options={modifiedDealers}
                  style={{ width: "100%" }}
                  disabled={
                    hasRole(4, 5, 6)
                      ? false
                      : filter.OemNo === undefined ||
                        filter.OemNo === null ||
                        filter.OemNo === ""
                  }
                  allowClear
                />
              </Col>
            )}
            {!hasRole(9, 10, 11, 12) && (
              <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Select
                  showSearch
                  placeholder="Customer"
                  value={
                    filter.CustomerNo ? Number(filter?.CustomerNo) : undefined
                  }
                  optionFilterProp="CustomerNo"
                  onChange={(value) =>
                    setFilter({ ...filter, CustomerNo: value })
                  }
                  onSearch={onSearch}
                  options={modifiedCustomers}
                  style={{ width: "100%" }}
                  disabled={
                    hasRole(7, 8, 9)
                      ? false
                      : filter.DealerNo === undefined ||
                        filter.DealerNo === null ||
                        filter.DealerNo === ""
                  }
                  allowClear
                />
              </Col>
            )}
            {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Input
              className="input_text_3 mr10 !h-full"
              name="AccountName"
              placeholder="Account"
              value={filter.AccountName}
              onChange={handleFilterChange}
            />
          </Col> */}
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                placeholder="Node Type"
                optionFilterProp="label"
                value={filter.NodeType ? Number(filter?.NodeType) : undefined}
                onChange={(value) => setFilter({ ...filter, NodeType: value })}
                onSearch={onSearch}
                options={modifiedNodeTypes}
                style={{ width: "100%" }}
                allowClear
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                placeholder="Site Type"
                optionFilterProp="label"
                value={
                  filter.SiteType || filter.SiteType === 0
                    ? Number(filter.SiteType)
                    : undefined
                }
                onChange={(value) => setFilter({ ...filter, SiteType: value })}
                onSearch={onSearch}
                options={modifiedSiteTypes}
                style={{ width: "100%" }}
                allowClear
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
              <Input
                className="input_text_3 mr10 !h-full"
                name="Mac"
                placeholder="MAC"
                value={filter.Mac}
                onChange={handleFilterChange}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                placeholder="Product"
                optionFilterProp="Product"
                value={filter.Product && Number(filter.Product)}
                onChange={(value) => setFilter({ ...filter, Product: value })}
                onSearch={onSearch}
                options={modifiedProducts}
                style={{ width: "100%" }}
                allowClear
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                placeholder="Model"
                optionFilterProp="Model"
                value={
                  filter.Model?.toString() ? Number(filter.Model) : undefined
                }
                onChange={(value) => setFilter({ ...filter, Model: value })}
                onSearch={onSearch}
                options={modifiedModels}
                style={{ width: "100%" }}
                allowClear
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
              <Input
                className="input_text_3 mr10 !h-full"
                name="Type"
                placeholder="Door"
                value={filter.Type}
                onChange={(e) => {
                  const { value } = e.target;
                  // Allow only positive integers or empty string
                  if (/^[1-9]\d*$/.test(value) || value === "") {
                    handleFilterChange(e);
                  }
                }}
              />
            </Col>
            {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                placeholder="License Option"
                optionFilterProp="LicenseOption"
                onChange={(value) =>
                  setFilter({ ...filter, LicenseOption: value })
                }
                value={
                  filter.LicenseOption || filter.LicenseOption === 0
                    ? Number(filter.LicenseOption)
                    : undefined
                }
                onSearch={onSearch}
                options={modifiedLicenseOptions}
                style={{ width: "100%" }}
                allowClear
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                placeholder="License Device"
                optionFilterProp="label"
                value={filter.LicenseDevice || undefined}
                onChange={(value) =>
                  setFilter({ ...filter, LicenseDevice: value })
                }
                onSearch={onSearch}
                options={modifiedLicenseDevices}
                style={{ width: "100%" }}
                allowClear
              />
            </Col> */}
            {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              showSearch
              placeholder="License Device"
              value={selectedDeviceFilter}
              onChange={(value) => {
                // setFilter({ ...filter, LicenseDevices: value })
                setHasSelected(true);
                setSelectedDeviceFilter(value);
                if (value) {
                  setFilter({
                    ...filter,
                    [value]: null,
                  });
                } else {
                  // When no device is selected, remove all device keys but keep other filters
                  const deviceKeys = [
                    "Channel",
                    "Facegate",
                    "Lockset",
                    "ContLock",
                    "Subnode",
                    "Intercom",
                    "Sicucom",
                    "Invitation",
                    // "Storage",
                  ];
                  const newFilter = { ...filter };

                  // Remove all device keys
                  deviceKeys.forEach((key) => {
                    delete newFilter[key];
                  });

                  setFilter(newFilter);
                  setSelectedDeviceFilter(null);
                }
              }}
              onSearch={onSearch}
              options={LicenseDeviceOptions}
              style={{ width: "100%" }}
              allowClear
            />
          </Col> */}
            {/* {selectedDeviceFilter && (
            <Col xs={24} sm={24} md={12} lg={8} xl={6}>
              <Select
                showSearch
                placeholder="License Device Options"
                optionFilterProp="LicenseDevices"
                value={filter[selectedDeviceFilter] || undefined}
                onChange={(value) => {
                  // Get all device keys to remove (except the current one)
                  const deviceKeys = [
                    "Channel",
                    "Facegate",
                    "Lockset",
                    "ContLock",
                    "Subnode",
                    "Intercom",
                    "Sicucom",
                    "Invitation",
                    "Storage",
                  ];

                  // Create new filter object without other device keys
                  const newFilter = { ...filter };

                  // Remove all device keys except the current selected one
                  deviceKeys.forEach((key) => {
                    if (key !== selectedDeviceFilter) {
                      delete newFilter[key];
                    }
                  });

                  // Set the new value for the selected device
                  if (value) {
                    newFilter[selectedDeviceFilter] = value;
                  } else {
                    delete newFilter[selectedDeviceFilter];
                  }

                  setFilter(newFilter);
                }}
                onSearch={onSearch}
                options={modifiedDeviceOptions[selectedDeviceFilter]}
                style={{ width: "100%" }}
                allowClear
              />
            </Col>
          )} */}
            {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Input
              className="input_text_3 mr10 !h-full"
              name="Type"
              placeholder="Type"
              value={filter.Type}
              onChange={handleFilterChange}
            />
          </Col> */}
            {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              showSearch
              placeholder="OEM"
              value={filter.OemNo || undefined}
              optionFilterProp="OemNo"
              onChange={(value) => setFilter({ ...filter, OemNo: value })}
              onSearch={onSearch}
              options={modifiedOems}
              style={{ width: "100%" }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              showSearch
              placeholder="Dealer"
              value={filter.DealerNo || undefined}
              optionFilterProp="DealerNo"
              onChange={(value) => setFilter({ ...filter, DealerNo: value })}
              onSearch={onSearch}
              options={modifiedDealers}
              style={{ width: "100%" }}
              disabled={
                filter.OemNo === undefined ||
                filter.OemNo === null ||
                filter.OemNo === ""
              }
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              showSearch
              placeholder="Customer"
              value={filter.CustomerNo || undefined}
              optionFilterProp="CustomerNo"
              onChange={(value) => setFilter({ ...filter, CustomerNo: value })}
              onSearch={onSearch}
              options={modifiedCustomers}
              style={{ width: "100%" }}
              disabled={
                filter.DealerNo === undefined ||
                filter.DealerNo === null ||
                filter.DealerNo === ""
              }
              allowClear
            />
          </Col> */}
          </Row>
        </div>
        <div className="flex justify-between my-4 overflow-x-auto !items-center">
          <div className="flex items-center">
            <Button
              type="primary"
              className="mr-2 w-[104px] h-[30px] font-medium"
              onClick={handleFilter}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              Apply
            </Button>
            <Button
              className="!mr-2 reset-button"
              onClick={() => {
                form.resetFields();
                setSelectedDeviceFilter(null);
                handleReset();
              }}
            >
              <HiArrowPath size={14} />
              Reset
            </Button>
            <Button
              type="primary"
              className="w-[104px] h-[30px] font-medium"
              onClick={() => setShowCommonModal(true)}
            >
              <HiOutlineViewColumns className="!text-[14px] font-medium" />
              Column
            </Button>
          </div>
        </div>
      </Form>
    </>
  );
};
